import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { fetchThanksgivingMonitoringReportURL } from "../../const";
import {
  getSelectedInterval,
  loadThanksgivingConfig,
} from "../../utils/thanksgivingConfigStorage";
import {
  defaultDateRangeFromReports,
  parseThanksgivingWorkbookForInterval,
} from "./parseReportWorkbook";
import "../batch-monitor/index.css";
import "./index.css";

function isCurrencyValue(value) {
  return typeof value === "string" && /USD|CAD/i.test(value);
}

/** "USD 588,211.94  + CAD 132,116.67" → ["USD 588,211.94", "+ CAD 132,116.67"] */
function splitCurrencyParts(value) {
  return String(value)
    .split("+")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part, i) => (i === 0 ? part : `+ ${part}`));
}

function formatMetric(value) {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "number") return value.toLocaleString();
  if (isCurrencyValue(value)) {
    return splitCurrencyParts(value).map((part, i) => (
      <span key={part + i} className="tg-currency-line">
        {part}
      </span>
    ));
  }
  return String(value);
}

/** Parse interval label "26/11/24 4:00 AM" → "2024-11-26" */
function intervalLabelToIsoDate(label) {
  if (!label) return null;
  const m = String(label).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})\b/);
  if (!m) return null;
  const [, dd, mm, yy] = m;
  const year = yy.length === 2 ? `20${yy}` : yy;
  return `${year}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
}

function formatDisplayDate(isoDate) {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  if (!y || !m || !d) return isoDate;
  return `${d}/${m}/${y}`;
}

function filenameFromContentDisposition(header) {
  if (!header) return null;
  const utf = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf?.[1]) {
    try {
      return decodeURIComponent(utf[1].trim().replace(/["']/g, ""));
    } catch {
      return utf[1].trim().replace(/["']/g, "");
    }
  }
  const plain = header.match(/filename\s*=\s*("?)([^";]+)\1/i);
  return plain?.[2]?.trim() || null;
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || "Thanks-Giving-Monitoring.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const EMPTY_REPORT = {
  reportTitle: "Thanks Giving Monitoring",
  subtitle: "",
  caption: "",
  groupHeader: "No of Idocs - cumulative by day (starting 00:00)",
  metricHeaders: ["Success", "Error", "Total", "Pending"],
  intervals: [],
  rows: [],
};

const DEFAULT_INTERVAL_FILTERS = {
  hours: "2",
  rows: [],
};

const INTERVAL_TABS = [
  { key: "twoHour", label: "2 Hourly", interval: "2" },
  { key: "fourHour", label: "4 Hourly", interval: "4" },
];

async function fetchThanksgivingReportBuffer(interval) {
  const response = await fetch(fetchThanksgivingMonitoringReportURL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ interval: String(interval) }),
  });
  if (!response.ok) {
    throw new Error(`Failed to load ${interval}-hour report (${response.status})`);
  }
  const buffer = await response.arrayBuffer();
  const blob = new Blob([buffer], {
    type:
      response.headers.get("content-type") ||
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const fileName =
    filenameFromContentDisposition(response.headers.get("content-disposition")) ||
    `Thanks-Giving-Monitoring-${interval}hr.xlsx`;
  return { buffer, blob, fileName };
}

function normalizeTgStatus(value) {
  const raw = String(value ?? "").trim().toLowerCase();
  if (raw === "outbound") return "Outbound";
  if (raw === "inbound") return "Inbound";
  return "";
}

function reportModeFromHours(hours) {
  return String(hours ?? "").trim() === "4" ? "fourHour" : "twoHour";
}

function ThanksgivingMonitoring() {
  const [reportMode, setReportMode] = useState("fourHour");
  const [reports, setReports] = useState({});
  const [loadState, setLoadState] = useState("loading"); // loading | ready | error
  const [loadError, setLoadError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const reportBlobsRef = useRef({ twoHour: null, fourHour: null });
  const reportFileNamesRef = useRef({ twoHour: "", fourHour: "" });

  const [intervalFilters, setIntervalFilters] = useState(DEFAULT_INTERVAL_FILTERS);

  const [draftStartDate, setDraftStartDate] = useState("");
  const [draftEndDate, setDraftEndDate] = useState("");
  const [draftDirection, setDraftDirection] = useState("");
  const [draftRicef, setDraftRicef] = useState("");

  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const [appliedDirection, setAppliedDirection] = useState("");
  const [appliedRicef, setAppliedRicef] = useState("");

  const loadReport = useCallback(async () => {
    setLoadState("loading");
    setLoadError("");
    try {
      const merged = {};
      const errors = [];

      await Promise.all(
        INTERVAL_TABS.map(async ({ key, interval }) => {
          try {
            const { buffer, blob, fileName } = await fetchThanksgivingReportBuffer(interval);
            const part = parseThanksgivingWorkbookForInterval(buffer, interval);
            Object.assign(merged, part);
            reportBlobsRef.current[key] = blob;
            reportFileNamesRef.current[key] = fileName;
          } catch (err) {
            console.warn(`Thanksgiving ${interval}hr report failed:`, err);
            errors.push(err?.message || `${interval}-hour report failed`);
            reportBlobsRef.current[key] = null;
            reportFileNamesRef.current[key] = "";
          }
        })
      );

      if (!merged.twoHour && !merged.fourHour) {
        throw new Error(errors.join("; ") || "Failed to load 2hr and 4hr reports");
      }

      const range = defaultDateRangeFromReports(merged);
      setReports(merged);

      const preferredMode = reportModeFromHours(
        getSelectedInterval() || intervalFilters.hours || "2"
      );
      if (merged[preferredMode]) {
        setReportMode(preferredMode);
      } else if (merged.fourHour) {
        setReportMode("fourHour");
      } else if (merged.twoHour) {
        setReportMode("twoHour");
      }

      setDraftStartDate(range.start);
      setDraftEndDate(range.end);
      setAppliedStartDate(range.start);
      setAppliedEndDate(range.end);
      setDraftRicef("");
      setAppliedRicef("");

      setLoadState("ready");
    } catch (err) {
      console.error("Thanksgiving monitoring load failed:", err);
      setLoadError(err?.message || "Failed to load report");
      setLoadState("error");
      setReports({});
      reportBlobsRef.current = { twoHour: null, fourHour: null };
      reportFileNamesRef.current = { twoHour: "", fourHour: "" };
    }
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  useEffect(() => {
    const config = loadThanksgivingConfig();
    setIntervalFilters({
      hours: getSelectedInterval() || config.hours || config.interval || DEFAULT_INTERVAL_FILTERS.hours,
      rows: Array.isArray(config.rows) ? config.rows : [],
    });
  }, []);

  const data = reports[reportMode] || EMPTY_REPORT;

  const allIntervals = useMemo(() => data.intervals || [], [data]);
  const allRows = useMemo(() => data.rows || [], [data]);

  const ricefOptions = useMemo(() => {
    const set = new Set();
    allRows.forEach((r) => {
      if (r.type === "section") return;
      if (r.ricefId) set.add(r.ricefId);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allRows]);

  // When switching 2hr/4hr, reset row filters (keep date range)
  useEffect(() => {
    setDraftDirection("");
    setDraftRicef("");
    setAppliedDirection("");
    setAppliedRicef("");
  }, [reportMode]);

  const rangeInvalid =
    !draftStartDate || !draftEndDate || draftStartDate > draftEndDate;

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (rangeInvalid) return;
      setAppliedStartDate(draftStartDate);
      setAppliedEndDate(draftEndDate);
      setAppliedDirection(draftDirection);
      setAppliedRicef(draftRicef);
    },
    [draftStartDate, draftEndDate, draftDirection, draftRicef, rangeInvalid]
  );

  const intervals = useMemo(() => {
    if (!appliedStartDate || !appliedEndDate) return allIntervals;
    return allIntervals.filter((interval) => {
      const iso = intervalLabelToIsoDate(interval.label);
      if (!iso) return false;
      return iso >= appliedStartDate && iso <= appliedEndDate;
    });
  }, [allIntervals, appliedStartDate, appliedEndDate]);

  const matchesDirection = useCallback((row, direction) => {
    if (!direction) return true;
    const key = row.directionKey || "";
    if (direction === "Inbound") {
      return key === "Inbound" || key === "I/B Proxy";
    }
    if (direction === "Outbound") {
      return key === "Outbound" || key === "O/B Proxy";
    }
    return key === direction;
  }, []);

  const rows = useMemo(() => {
    const configuredEntries = intervalFilters.rows || [];
    return allRows.filter((row) => {
      if (row.type === "section" || row.type === "total") return true;

      if (configuredEntries.length) {
        const matchesConfig = configuredEntries.some((entry) => {
          const ricefOk =
            !entry.ricefId ||
            String(row.ricefId || "").trim().toLowerCase() === String(entry.ricefId).trim().toLowerCase();
          const dir = normalizeTgStatus(entry.direction);
          const dirOk = !dir || matchesDirection(row, dir);
          return ricefOk && dirOk;
        });
        if (!matchesConfig) return false;
      }

      if (row.type === "summary") {
        if (appliedRicef && row.ricefId !== appliedRicef) return false;
        if (appliedDirection) return false;
        return true;
      }

      if (!matchesDirection(row, appliedDirection)) return false;
      if (appliedRicef && row.ricefId !== appliedRicef) return false;
      return true;
    });
  }, [allRows, appliedDirection, appliedRicef, intervalFilters.rows, matchesDirection]);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const cachedBlob = reportBlobsRef.current[reportMode];
      const cachedName = reportFileNamesRef.current[reportMode];
      if (cachedBlob) {
        downloadBlob(cachedBlob, cachedName || "Thanks-Giving-Monitoring.xlsx");
        return;
      }
      const tab = INTERVAL_TABS.find((item) => item.key === reportMode) || INTERVAL_TABS[0];
      const { blob, fileName } = await fetchThanksgivingReportBuffer(tab.interval);
      reportBlobsRef.current[reportMode] = blob;
      reportFileNamesRef.current[reportMode] = fileName;
      downloadBlob(blob, fileName);
    } catch (err) {
      console.error("Thanksgiving monitoring download failed:", err);
      setLoadError(err?.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  }, [reportMode]);

  const hasTwoHour = Boolean(reports.twoHour);
  const hasFourHour = Boolean(reports.fourHour);

  return (
    <div className="batch-monitor-page tg-monitor-page">
      <div className="batch-monitor-header tg-monitor-header">
        <div>
          <h1 className="batch-monitor-title">{data.reportTitle || "Thanks Giving Monitoring"}</h1>
        </div>

        <div className="tg-report-radio" role="radiogroup" aria-label="Report interval">
          <label
            className={`tg-radio-option ${reportMode === "twoHour" ? "active" : ""} ${
              !hasTwoHour ? "tg-radio-disabled" : ""
            }`}
          >
            <input
              type="radio"
              name="tg-report-mode"
              value="twoHour"
              checked={reportMode === "twoHour"}
              disabled={!hasTwoHour || loadState !== "ready"}
              onChange={() => setReportMode("twoHour")}
            />
            2 Hourly
          </label>
          <label
            className={`tg-radio-option ${reportMode === "fourHour" ? "active" : ""} ${
              !hasFourHour ? "tg-radio-disabled" : ""
            }`}
          >
            <input
              type="radio"
              name="tg-report-mode"
              value="fourHour"
              checked={reportMode === "fourHour"}
              disabled={!hasFourHour || loadState !== "ready"}
              onChange={() => setReportMode("fourHour")}
            />
            4 Hourly
          </label>
        </div>
      </div>

      <form className="tg-filters-bar" onSubmit={handleSubmit}>
        <label className="filter-label">
          From
          <input
            type="date"
            className="filter-select tg-date-input"
            value={draftStartDate}
            max={draftEndDate || undefined}
            onChange={(e) => setDraftStartDate(e.target.value)}
            disabled={loadState !== "ready"}
            aria-label="Start date"
          />
        </label>
        <label className="filter-label">
          To
          <input
            type="date"
            className="filter-select tg-date-input"
            value={draftEndDate}
            min={draftStartDate || undefined}
            onChange={(e) => setDraftEndDate(e.target.value)}
            disabled={loadState !== "ready"}
            aria-label="End date"
          />
        </label>
        <label className="filter-label">
          Direction
          <select
            className="filter-select tg-filter-select"
            value={draftDirection}
            onChange={(e) => setDraftDirection(e.target.value)}
            disabled={loadState !== "ready"}
            aria-label="Filter by direction"
          >
            <option value="">All</option>
            <option value="Inbound">Inbound</option>
            <option value="Outbound">Outbound</option>
          </select>
        </label>
        <label className="filter-label tg-filter-ricef">
          RICEF ID
          <select
            className="filter-select tg-filter-select tg-ricef-select"
            value={draftRicef}
            onChange={(e) => setDraftRicef(e.target.value)}
            disabled={loadState !== "ready"}
            aria-label="Filter by RICEF ID"
          >
            <option value="">All</option>
            {ricefOptions.map((id) => (
              <option key={id} value={id} title={id}>
                {id}
              </option>
            ))}
          </select>
        </label>
        <div className="tg-filter-actions">
          <button
            type="submit"
            className="filter-btn active tg-submit-btn"
            disabled={loadState !== "ready" || rangeInvalid}
          >
            Submit
          </button>
          <button
            type="button"
            className="filter-btn tg-download-btn"
            onClick={handleDownload}
            disabled={loadState === "loading" || downloading}
          >
            {downloading ? "Downloading…" : "Download Report"}
          </button>
        </div>
      </form>

      <div className="table-container tg-monitor-table-container">
        {loadState === "loading" ? (
          <div className="tg-empty-state">
            <p>Loading Thanksgiving monitoring report…</p>
          </div>
        ) : loadState === "error" ? (
          <div className="tg-empty-state">
            <p>{loadError || "Failed to load report."}</p>
            <button type="button" className="filter-btn active" onClick={loadReport}>
              Retry
            </button>
          </div>
        ) : intervals.length === 0 ? (
          <div className="tg-empty-state">
            <p>
              No monitoring data for {formatDisplayDate(appliedStartDate)}
              {appliedStartDate !== appliedEndDate
                ? ` – ${formatDisplayDate(appliedEndDate)}`
                : ""}
              .
            </p>
          </div>
        ) : (
          <div className="table-scroll tg-monitor-scroll">
            <table className="job-monitor-table tg-monitor-table">
              <thead>
                <tr className="tg-head-group">
                  <th className="tg-sticky tg-sticky-1 tg-th-ricef">RICEF ID</th>
                  <th className="tg-sticky tg-sticky-2 tg-th-blank" />
                  <th className="tg-sticky tg-sticky-3 tg-th-blank" />
                  {intervals.map((interval) => (
                    <th key={`g-${interval.id}`} colSpan={4} className="tg-th-group">
                      {data.groupHeader}
                    </th>
                  ))}
                </tr>

                <tr className="tg-head-time">
                  <th className="tg-sticky tg-sticky-1 tg-th-caption">{data.subtitle}</th>
                  <th className="tg-sticky tg-sticky-2 tg-th-dir-label">In/Out bound</th>
                  <th className="tg-sticky tg-sticky-3 tg-th-variant-label">VARIANT (IDOC)</th>
                  {intervals.map((interval) => (
                    <th key={`t-${interval.id}`} colSpan={4} className="tg-th-time">
                      {interval.label}
                    </th>
                  ))}
                </tr>

                <tr className="tg-head-metrics">
                  <th className="tg-sticky tg-sticky-1 tg-th-subcaption">{data.caption}</th>
                  <th className="tg-sticky tg-sticky-2 tg-th-blank" />
                  <th className="tg-sticky tg-sticky-3 tg-th-blank" />
                  {intervals.map((interval) =>
                    data.metricHeaders.map((metric, mi) => (
                      <th
                        key={`m-${interval.id}-${metric}-${mi}`}
                        className={`tg-th-metric ${
                          mi === 0
                            ? "tg-metric-success"
                            : mi === 1
                              ? "tg-metric-error"
                              : mi === 3
                                ? "tg-metric-pending"
                                : "tg-metric-total"
                        }`}
                      >
                        {metric}
                      </th>
                    ))
                  )}
                </tr>
              </thead>

              <tbody>
                {rows.map((row, idx) => {
                  if (row.type === "section") {
                    return (
                      <tr key={`sec-${idx}`} className="tg-row-section">
                        <td className="tg-sticky tg-sticky-1" colSpan={3 + intervals.length * 4}>
                          {row.ricefId}
                        </td>
                      </tr>
                    );
                  }

                  const rowClass =
                    row.type === "total"
                      ? "tg-row-total"
                      : row.type === "summary"
                        ? "tg-row-summary"
                        : row.type === "proxy"
                          ? "tg-row-proxy"
                          : "tg-row-idoc";

                  return (
                    <tr key={`${row.ricefId}-${idx}`} className={rowClass}>
                      <td className="tg-sticky tg-sticky-1 tg-cell-ricef">
                        <div className="tg-cell-scroll">{row.ricefId}</div>
                      </td>
                      <td className="tg-sticky tg-sticky-2 tg-cell-dir">
                        <div className="tg-dir-text">{row.direction}</div>
                      </td>
                      <td className="tg-sticky tg-sticky-3 tg-cell-variant">
                        <div className="tg-cell-scroll">{row.variant}</div>
                      </td>
                      {intervals.map((interval) => {
                        const m = row.metrics?.[interval.id] || {};
                        const values = [m.success, m.error, m.total, m.pending];
                        return values.map((value, vi) => (
                          <td
                            key={`${interval.id}-${vi}`}
                            className={`tg-cell-metric ${
                              vi === 0
                                ? "tg-cell-success"
                                : vi === 1
                                  ? "tg-cell-error"
                                  : vi === 3
                                    ? "tg-cell-pending"
                                    : ""
                            } ${isCurrencyValue(value) ? "tg-cell-currency" : ""}`}
                          >
                            {formatMetric(value)}
                          </td>
                        ));
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ThanksgivingMonitoring;
